import 'moment-timezone'

import {
  CampaignPostDTO,
  CampaignStatus,
  ContentItemDTO,
  ContentKind,
  MediaFormat,
  PostMediaDTO,
  PostState,
  PostStatus,
  PostTextStatus,
  SOCIAL_NETWORK_ORDER,
  SocialCampaignDTO,
  SocialNetwork,
  UpdateCampaignPostDTO,
  VisualKeyDTO
} from 'models/socialCampaign'

import { dayLocaleOf } from './campaignStatus'
import moment from 'moment'
import { PostSlot, postSlotOf } from './schedule'

/**
 * The post editor (ISSUE-3624, prototype v3 `renderEditor`, v2 decisions 1, 12 and 20): its address, its modes, what a
 * post still lets the client change, the review's walk and its launch, and the change a save sends.
 */

// ============================================================================
// ADDRESS
// ============================================================================

/**
 * The editor's address in the URL hash (v2 principle 3): `#post=<id>`, or `#post=new&date=…&time=…` for a post written
 * by hand (the calendar's free hour gives its date and time, ISSUE-3649). Back closes it, a reload reopens it.
 */
export type PostAddress =
  | { kind: 'post', postId: string }
  | { kind: 'new', date: string, time: string }

const DAY = /^\d{4}-\d{2}-\d{2}$/
const TIME = /^\d{2}:\d{2}$/

/** The address a hash holds, or null when it holds none (any other hash is the page's, left alone). */
export const parsePostAddress = (hash: string): PostAddress | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const post = params.get('post')
  if (!post) return null
  if (post !== 'new') return { kind: 'post', postId: post }
  const date = params.get('date') ?? ''
  const time = params.get('time') ?? ''
  return { kind: 'new', date: DAY.test(date) ? date : '', time: TIME.test(time) ? time : '' }
}

/** The hash of an address, without its `#`. */
export const formatPostAddress = (address: PostAddress) => {
  if (address.kind === 'post') return `post=${encodeURIComponent(address.postId)}`
  return ['post=new', address.date && `date=${address.date}`, address.time && `time=${address.time}`].filter(Boolean).join('&')
}

// ============================================================================
// MODES AND WHAT A POST STILL TAKES
// ============================================================================

/** Review a draft (approve and walk), edit any other post, or create one by hand: one layout, three feet. */
export type EditorMode = 'review' | 'edit' | 'create'

export const editorModeOf = (post: CampaignPostDTO | null): EditorMode => {
  if (!post) return 'create'
  return post.status === PostStatus.DRAFT ? 'review' : 'edit'
}

type CampaignClock = Pick<SocialCampaignDTO, 'status' | 'pausedAt' | 'editFreezeMinutes'>

/**
 * Where a post stands now, as the backend reads it (`SocialCampaignPost.state`): the answer's `state` is frozen at its
 * instant, while a scheduled post freezes, goes out or misses its time as the clock runs. A draft campaign holds its
 * scheduled posts (written by hand) for ever, a paused one since its pause.
 */
export const postStateAt = (post: CampaignPostDTO, campaign: CampaignClock, now: Date): PostState => {
  switch (post.status) {
    case PostStatus.DRAFT: return PostState.DRAFT
    case PostStatus.WAITING_FOR_CONTENT: return PostState.WAITING_FOR_CONTENT
    case PostStatus.SKIPPED: return PostState.SKIPPED
    case PostStatus.SCHEDULED: {
      const at = Date.parse(post.publishAt)
      const heldSince = campaign.status === CampaignStatus.DRAFT
        ? Number.NEGATIVE_INFINITY
        : campaign.pausedAt ? Date.parse(campaign.pausedAt) : null
      const freeze = campaign.editFreezeMinutes * 60_000
      if (at > now.getTime() + freeze) return PostState.SCHEDULED
      if (heldSince !== null && at > now.getTime()) return PostState.SCHEDULED
      if (at > now.getTime()) return PostState.FROZEN
      if (heldSince !== null && at >= heldSince) return PostState.MISSED
      return PostState.SENT
    }
  }
}

/** What the client can still change of a post (backend `PostTransitions`). */
export interface PostEditability {
  /** Text, visuals and networks */
  content: boolean
  /** Date and time */
  schedule: boolean
  removable: boolean
  /** Not published (its time came during a pause, or after the end): a change needs a new date or time */
  needsNewTime: boolean
  /** Why nothing can be changed, when nothing can */
  locked: 'ended' | 'frozen' | 'sent' | null
}

const NOTHING: Omit<PostEditability, 'locked'> = { content: false, schedule: false, removable: false, needsNewTime: false }

export const postEditabilityOf = (post: CampaignPostDTO, campaign: CampaignClock, now: Date): PostEditability => {
  if (campaign.status === CampaignStatus.ENDED) return { ...NOTHING, locked: 'ended' }
  switch (postStateAt(post, campaign, now)) {
    case PostState.DRAFT:
    case PostState.SCHEDULED:
      return { content: true, schedule: true, removable: true, needsNewTime: false, locked: null }
    case PostState.WAITING_FOR_CONTENT:
      // written once the ordered content arrives: it only moves meanwhile
      return { content: false, schedule: true, removable: true, needsNewTime: false, locked: null }
    case PostState.MISSED:
    case PostState.SKIPPED:
      return { content: true, schedule: true, removable: false, needsNewTime: true, locked: null }
    case PostState.FROZEN:
      return { ...NOTHING, locked: 'frozen' }
    case PostState.SENT:
      return { ...NOTHING, locked: 'sent' }
  }
}

// ============================================================================
// THE REVIEW'S WALK
// ============================================================================

const isDraft = (post: CampaignPostDTO) => post.status === PostStatus.DRAFT

/**
 * Where the review stands for a draft (prototype `reviewState`): how many other drafts are still to decide, whether it
 * is the last (approving it schedules every draft, launching a draft campaign), and whether removing it would launch
 * (the others are all approved).
 */
export interface ReviewState {
  left: number
  last: boolean
  removeLaunches: boolean
}

export const reviewStateOf = (post: CampaignPostDTO, posts: CampaignPostDTO[]): ReviewState => {
  const others = posts.filter((other) => isDraft(other) && other.id !== post.id)
  const left = others.filter((other) => !other.reviewed).length
  return { left, last: left === 0, removeLaunches: left === 0 && others.length > 0 }
}

/** The posts either side of one in publication order, and its place: "Post 2 / 7" walks every post. */
export const neighboursOf = (posts: CampaignPostDTO[], postId: string) => {
  const index = posts.findIndex((post) => post.id === postId)
  return {
    index,
    total: posts.length,
    previous: index > 0 ? posts[index - 1] : null,
    next: index !== -1 && index < posts.length - 1 ? posts[index + 1] : null,
  }
}

/** "Approve & next" walks to the first draft still to decide, other than the one just approved. */
export const nextDraftToDecide = (posts: CampaignPostDTO[], exceptId?: string) =>
  posts.find((post) => isDraft(post) && !post.reviewed && post.id !== exceptId) ?? null

/** After a removal while reviewing, the walk goes on: the post after it, else the one before (prototype `ed-remove`). */
export const afterRemoval = (posts: CampaignPostDTO[], postId: string) => {
  const { next, previous } = neighboursOf(posts, postId)
  return next ?? previous
}

// ============================================================================
// THE FORM
// ============================================================================

/** A visual of the post being edited: its identity, how to draw it, and nothing the backend would refuse. */
export interface EditorMedia {
  key: VisualKeyDTO
  format: MediaFormat
  contentKind: ContentKind
  label: string | null
  /** Null for a video, a document, or a visual the viewer's seat does not show */
  thumbnailUrl: string | null
  previewUrl: string | null
}

/** What the form holds; the post's day and time are the campaign's timezone. */
export interface PostDraft {
  /** YYYY-MM-DD, empty until picked */
  day: string
  /** HH:mm, empty until picked */
  time: string
  /** In network order */
  networks: SocialNetwork[]
  /** In the order they go out */
  media: EditorMedia[]
  text: string
}

export const visualKeyOf = ({ assignmentPublicId, filename, visualType }: VisualKeyDTO): VisualKeyDTO => ({ assignmentPublicId, filename, visualType })

export const sameVisual = (a: VisualKeyDTO, b: VisualKeyDTO) =>
  a.assignmentPublicId === b.assignmentPublicId && a.filename === b.filename && a.visualType === b.visualType

export const editorMediaOfPost = (media: PostMediaDTO): EditorMedia => ({
  key: visualKeyOf(media.reference),
  format: media.reference.format,
  contentKind: media.reference.contentKind,
  label: media.reference.label,
  thumbnailUrl: media.thumbnailUrl,
  previewUrl: media.previewUrl,
})

export const editorMediaOfItem = (item: ContentItemDTO): EditorMedia => ({
  key: visualKeyOf(item),
  format: item.format,
  contentKind: item.contentKind,
  label: item.label,
  thumbnailUrl: item.thumbnailUrl,
  previewUrl: item.previewUrl,
})

export const inNetworkOrder = (networks: SocialNetwork[]) => SOCIAL_NETWORK_ORDER.filter((network) => networks.includes(network))

/**
 * A post the campaign's networks change did not re-target (missed, skipped) keeps those the campaign dropped: the form
 * holds the campaign's ones only, the campaign's when none is left, as the backend does on a new date (`networksStill`).
 */
const networksStill = (networks: SocialNetwork[], campaignNetworks: SocialNetwork[]) => {
  const kept = networks.filter((network) => campaignNetworks.includes(network))
  return inNetworkOrder(kept.length ? kept : campaignNetworks)
}

export const draftOfPost = (post: CampaignPostDTO, timezone: string, campaignNetworks: SocialNetwork[]): PostDraft => {
  const slot = postSlotOf(post, timezone)
  return {
    day: slot.day,
    time: slot.time,
    networks: networksStill(post.networks, campaignNetworks),
    media: [...post.media].sort((a, b) => a.position - b.position).map(editorMediaOfPost),
    text: post.text,
  }
}

/** A post written by hand starts on the campaign's networks, on the day and time its address gives. */
export const blankDraft = (campaign: Pick<SocialCampaignDTO, 'networks'>, day: string, time: string): PostDraft => ({
  day,
  time,
  networks: inNetworkOrder(campaign.networks),
  media: [],
  text: '',
})

const sameNetworks = (a: SocialNetwork[], b: SocialNetwork[]) => a.length === b.length && a.every((network, index) => network === b[index])
const sameMedia = (a: EditorMedia[], b: EditorMedia[]) => a.length === b.length && a.every((media, index) => sameVisual(media.key, b[index].key))

/**
 * A post written by hand that holds nothing of the client's yet (no text, no visual, the networks it started on): it
 * closes without asking. The day and time do not count: a slot alone is nothing written.
 */
export const isBlankDraft = (draft: PostDraft, campaign: Pick<SocialCampaignDTO, 'networks'>) =>
  !draft.text && !draft.media.length && sameNetworks(draft.networks, inNetworkOrder(campaign.networks))

/**
 * What a save sends: only what changed (an empty change is a 400). A new day keeps the time and a new time keeps the
 * day, so each goes alone. The visuals go whole, in order, those the viewer's seat does not show included untouched.
 */
export const changesOf = (draft: PostDraft, post: CampaignPostDTO, timezone: string, campaignNetworks: SocialNetwork[]): UpdateCampaignPostDTO => {
  const saved = draftOfPost(post, timezone, campaignNetworks)
  const changes: UpdateCampaignPostDTO = {}
  if (draft.text !== saved.text) changes.text = draft.text
  if (!sameMedia(draft.media, saved.media)) changes.media = draft.media.map((media) => media.key)
  if (!sameNetworks(draft.networks, saved.networks)) changes.networks = draft.networks
  if (draft.day && draft.day !== saved.day) changes.date = draft.day
  if (draft.time && draft.time !== saved.time) changes.time = draft.time
  return changes
}

export const isEmptyChange = (changes: UpdateCampaignPostDTO) => Object.keys(changes).length === 0

/** The instant a day and a time of the campaign's timezone stand for (a time a clock change skips moves forward). */
export const instantOf = (day: string, time: string, timezone: string) => moment.tz(`${day} ${time}`, 'YYYY-MM-DD HH:mm', timezone).toDate()

/** Today in the campaign's timezone: the date field's minimum. */
export const todayIn = (timezone: string, now: Date) => moment(now).tz(timezone).format('YYYY-MM-DD')

// ============================================================================
// TEXT AND BLOCKERS
// ============================================================================

/** Longest text each network publishes (backend `SocialNetwork.textLimit`): the one text must fit the strictest. */
export const TEXT_LIMITS: Record<SocialNetwork, number> = {
  [SocialNetwork.INSTAGRAM]: 2_200,
  [SocialNetwork.FACEBOOK]: 63_206,
  [SocialNetwork.LINKEDIN]: 3_000,
  [SocialNetwork.TIKTOK]: 2_200,
}

/** The strictest ticked network's limit, and which network sets it; Instagram's when none is ticked (prototype `limitOf`). */
export const textLimitOf = (networks: SocialNetwork[]) => {
  const ticked = networks.length ? inNetworkOrder(networks) : [SocialNetwork.INSTAGRAM]
  return ticked.reduce(
    (strictest, network) => TEXT_LIMITS[network] < strictest.limit ? { limit: TEXT_LIMITS[network], network } : strictest,
    { limit: Number.POSITIVE_INFINITY, network: SocialNetwork.INSTAGRAM }
  )
}

/** A count in the UI language ("2,200", "2.200"); runs in renders, so it never throws (`dayLocaleOf` guards the language). */
export const formatCount = (count: number, language: string) => {
  if (!Number.isFinite(count)) return '\u2014'
  return new Intl.NumberFormat(dayLocaleOf(language)).format(count)
}

/** The most visuals a post holds (backend `NetworkMediaRules.MAX_MEDIA`, TikTok's 35 photos). */
export const MAX_POST_MEDIA = 35

/**
 * The visuals of the kinds a network publishes together, in order, and how many of them it keeps (backend
 * `NetworkMediaRules.kept`, prototype v3 `keeps`): Instagram its images then its videos, up to 10, no PDF; Facebook its
 * photos, otherwise one video; LinkedIn one PDF, otherwise up to 20 images, otherwise one video; TikTok one video,
 * otherwise up to 35 photos.
 */
const takenBy = (network: SocialNetwork, media: EditorMedia[]): { taken: EditorMedia[], most: number } => {
  const images = media.filter((item) => item.format === MediaFormat.IMAGE)
  const videos = media.filter((item) => item.format === MediaFormat.VIDEO)
  const documents = media.filter((item) => item.format === MediaFormat.DOCUMENT)
  switch (network) {
    case SocialNetwork.INSTAGRAM: return { taken: [...images, ...videos], most: 10 }
    case SocialNetwork.FACEBOOK: return images.length ? { taken: images, most: Number.POSITIVE_INFINITY } : { taken: videos, most: 1 }
    case SocialNetwork.LINKEDIN:
      if (documents.length) return { taken: documents, most: 1 }
      return images.length ? { taken: images, most: 20 } : { taken: videos, most: 1 }
    case SocialNetwork.TIKTOK: return videos.length ? { taken: videos, most: 1 } : { taken: images, most: 35 }
  }
}

/** What each network publishes of a post's visuals, in order (see `takenBy`). */
export const keptBy = (network: SocialNetwork, media: EditorMedia[]): EditorMedia[] => {
  const { taken, most } = takenBy(network, media)
  return taken.slice(0, most)
}

/** A note under the preview: what a network leaves out of the post, or how it publishes it (copy `editor.preview.note.<code>`). */
export type PreviewNoteCode =
  /** Instagram, Facebook, TikTok: the PDF is left out */
  | 'no_pdf'
  /** Instagram: a single video goes out as a Reel */
  | 'reel'
  | 'facebook_photos_only'
  | 'linkedin_document'
  | 'linkedin_no_mix'
  | 'tiktok_no_mix'
  | 'tiktok_needs_media'
  /** The network keeps fewer visuals than the post has of the kinds it publishes (`count` of `total`) */
  | 'capped'
  /** The network keeps none of the visuals */
  | 'nothing'

export interface PreviewNote {
  code: PreviewNoteCode
  tone: 'warning' | 'info'
  /** `capped`: how many visuals the network keeps */
  count?: number
  /** `capped`: how many the post has of the kinds the network publishes */
  total?: number
}

/**
 * The notes the preview shows under its pills, network by network (prototype v3 `keeps`, branch for branch): what
 * `keptBy` leaves out and why, how Instagram publishes a single video, how many visuals a network's limit keeps, and a
 * warning when the network keeps nothing.
 */
export const previewNotesOf = (network: SocialNetwork, media: EditorMedia[]): PreviewNote[] => {
  const images = media.filter((item) => item.format === MediaFormat.IMAGE).length
  const videos = media.filter((item) => item.format === MediaFormat.VIDEO).length
  const documents = media.filter((item) => item.format === MediaFormat.DOCUMENT).length
  const { taken, most } = takenBy(network, media)
  const kept = taken.slice(0, most)
  const notes: PreviewNote[] = []
  const warn = (code: PreviewNoteCode) => notes.push({ code, tone: 'warning' })
  const tell = (code: PreviewNoteCode) => notes.push({ code, tone: 'info' })
  switch (network) {
    case SocialNetwork.INSTAGRAM:
      if (documents) warn('no_pdf')
      if (kept.length === 1 && videos === 1) tell('reel')
      break
    case SocialNetwork.FACEBOOK:
      if (images && videos) warn('facebook_photos_only')
      if (documents) warn('no_pdf')
      break
    case SocialNetwork.LINKEDIN:
      if (documents && (images || videos)) tell('linkedin_document')
      else if (images && videos) warn('linkedin_no_mix')
      break
    case SocialNetwork.TIKTOK:
      if (videos && images) warn('tiktok_no_mix')
      if (documents) warn('no_pdf')
      if (!kept.length && !documents) warn('tiktok_needs_media')
      break
  }
  if (kept.length < taken.length) notes.push({ code: 'capped', tone: 'warning', count: kept.length, total: taken.length })
  if (!kept.length && !notes.some(({ code }) => code === 'tiktok_needs_media')) warn('nothing')
  return notes
}

/** TikTok publishes photos (with a 90-character title) or one video: a post with no video is a photo post (prototype `tiktokIsPhoto`). */
export const isTikTokPhotoPost = (media: EditorMedia[]) => {
  const kept = keptBy(SocialNetwork.TIKTOK, media)
  return kept.length > 0 && kept.every((item) => item.format === MediaFormat.IMAGE)
}

/** About how many characters of a caption Instagram's feed shows before "more". */
export const INSTAGRAM_CAPTION_PREVIEW = 125

/**
 * Whether Instagram's feed cuts the caption behind "more": at its first line break, or past what the feed shows of it.
 * The card also measures its line where there is a layout (jsdom has none).
 */
export const isInstagramCaptionCut = (text: string) => {
  const caption = text.trim()
  return caption.includes('\n') || Array.from(caption).length > INSTAGRAM_CAPTION_PREVIEW
}

/** How many lines of a post's text a Facebook or LinkedIn card shows before "See more". */
export const FEED_TEXT_LINES = 4

/** About how many characters fill a line of a Facebook or LinkedIn card (35.2rem wide). */
export const FEED_LINE_CHARACTERS = 50

/**
 * Whether a Facebook or LinkedIn card cuts the text behind "See more": past its four lines, a long line counted as
 * the lines it wraps to. The card measures its lines instead where there is a layout (jsdom has none).
 */
export const isFeedTextCut = (text: string) => {
  const lines = text.trim().split('\n')
  const shown = lines.reduce((count, line) => count + Math.max(1, Math.ceil(Array.from(line).length / FEED_LINE_CHARACTERS)), 0)
  return shown > FEED_TEXT_LINES
}

/** A run of a post's text: plain, or a hashtag or @mention, which the networks colour. */
export interface TextRun {
  text: string
  isTag: boolean
}

// A tag starts the text or follows a character that is not part of a word (an e-mail address is no mention); its
// leading character is captured rather than looked behind, which older Safari cannot parse.
const TAG = /(^|[^\p{L}\p{N}_])(#[\p{L}\p{N}_]+|@[\p{L}\p{N}_](?:[\p{L}\p{N}_.]*[\p{L}\p{N}_])?)/gu

/** The post's text cut into its plain runs and its hashtags and @mentions, in order. */
export const textRunsOf = (text: string): TextRun[] => {
  const runs: TextRun[] = []
  let at = 0
  for (const match of text.matchAll(TAG)) {
    const start = (match.index ?? 0) + match[1].length
    if (start > at) runs.push({ text: text.slice(at, start), isTag: false })
    runs.push({ text: match[2], isTag: true })
    at = start + match[2].length
  }
  if (at < text.length) runs.push({ text: text.slice(at), isTag: false })
  return runs
}

/**
 * Why the post cannot be saved or scheduled as it stands; the reason shows beside the disabled button (prototype
 * `createBlocker`). Checked in the prototype's order, then what the backend would refuse anyway.
 */
export type PostBlocker =
  /** Create: a date and a time */
  | 'when'
  /** Not published: a change gives it a new date or time */
  | 'new_time'
  /** The new time is not more than the edit freeze ahead */
  | 'too_soon'
  | 'visual'
  | 'network'
  /** No ticked network publishes any of the visuals (backend NO_PUBLISHABLE_MEDIA), e.g. a PDF only on Instagram */
  | 'visual_unpublishable'
  | 'text'
  | 'text_too_long'

interface BlockerContext {
  mode: EditorMode
  /** The change a save would send (edit and review), null on create */
  changes: UpdateCampaignPostDTO | null
  /** Not published: a change needs a new date or time */
  needsNewTime: boolean
  /** The post is scheduled once saved (created, or changed or re-dated as a scheduled post): it must be publishable */
  goesOut: boolean
  timezone: string
  editFreezeMinutes: number
  now: Date
  /** The post's saved day and time (edit and review): a field left empty keeps it, as the backend does */
  saved: PostSlot | null
}

/**
 * Whether a post is scheduled once a change is saved (backend `editPost`): a scheduled post stays scheduled, a missed
 * one is scheduled again, a skipped one too unless never approved (it goes back to the drafts). Only then is it
 * checked as publishable; a draft may stay incomplete until it is approved.
 */
export const scheduledOnceSaved = (post: CampaignPostDTO, campaign: CampaignClock, now: Date) => {
  switch (postStateAt(post, campaign, now)) {
    case PostState.SCHEDULED:
    case PostState.MISSED:
      return true
    case PostState.SKIPPED:
      return post.reviewed
    default:
      return false
  }
}

export const postBlockerOf = (draft: PostDraft, context: BlockerContext): PostBlocker | null => {
  const { mode, changes } = context
  // nothing to save: Done or Next leaves the post as it is
  if (mode !== 'create' && (!changes || isEmptyChange(changes))) return null
  if (mode === 'create' && (!draft.day || !draft.time)) return 'when'
  if (context.needsNewTime && !changes?.date && !changes?.time) return 'new_time'
  const timeChanged = mode === 'create' || !!changes?.date || !!changes?.time
  const day = draft.day || context.saved?.day
  const time = draft.time || context.saved?.time
  if (timeChanged && day && time) {
    const earliest = context.now.getTime() + context.editFreezeMinutes * 60_000
    if (instantOf(day, time, context.timezone).getTime() <= earliest) return 'too_soon'
  }
  if (!context.goesOut) return null
  if (!draft.media.length) return 'visual'
  if (!draft.networks.length) return 'network'
  if (draft.networks.every((network) => !keptBy(network, draft.media).length)) return 'visual_unpublishable'
  if (!draft.text.trim()) return 'text'
  if (draft.text.length > textLimitOf(draft.networks).limit) return 'text_too_long'
  return null
}

// ============================================================================
// THE AI ACTIONS ON THE TEXT (ISSUE-3619)
// ============================================================================

/** Where the post's text stands with its last AI action; READY from a backend without ISSUE-3619. */
export const postTextStatusOf = (post: Pick<CampaignPostDTO, 'textStatus'>): PostTextStatus => post.textStatus ?? PostTextStatus.READY

/**
 * Whether the editor offers Regenerate, Shorter and More hashtags on a post (backend `requestText`): a post that exists
 * (not one being written by hand), whose text the client can change, still ahead (not a post to give a new time: the
 * backend refuses one that missed its time or was skipped) and not waiting for its order (written on delivery).
 */
export const offersTextActions = (mode: EditorMode, editability: Pick<PostEditability, 'content' | 'needsNewTime'>, waiting: boolean) =>
  mode !== 'create' && editability.content && !editability.needsNewTime && !waiting

// ============================================================================
// THE SWIPE BETWEEN POSTS (phone)
// ============================================================================

/**
 * A horizontal swipe walks the posts (v2 decision 20): the post follows the finger; letting go past a third of the
 * width, or in a flick still moving that way, moves; short of it the post springs back. At the first or last post it
 * gives only a fifth of the drag.
 */
export const SWIPE = {
  /** Movement before the gesture decides it is a swipe or a scroll */
  lockDistance: 8,
  /** Horizontal beats vertical by this much to be a swipe */
  lockRatio: 1.2,
  endResistance: 0.2,
  /** px per ms */
  flickSpeed: 0.5,
  flickDistance: 40,
  /** A flick is still moving when the finger lifts */
  flickWithinMs: 100,
} as const

export const swipeOffset = (dx: number, hasTarget: boolean) => hasTarget ? dx : dx * SWIPE.endResistance

/** 0 to 1: how far the swipe has gone towards moving */
export const swipeProgress = (offset: number, width: number) => width > 0 ? Math.min(Math.abs(offset) / (width / 3), 1) : 0

export interface SwipeRelease {
  offset: number
  width: number
  /** Speed of the last movement, px per ms (negative to the left) */
  velocity: number
  /** Since the last movement, ms */
  idleMs: number
  hasTarget: boolean
}

export const swipeMoves = ({ offset, width, velocity, idleMs, hasTarget }: SwipeRelease) => {
  if (!hasTarget) return false
  const flick = idleMs < SWIPE.flickWithinMs && Math.abs(velocity) > SWIPE.flickSpeed && (velocity < 0) === (offset < 0) && Math.abs(offset) > SWIPE.flickDistance
  return Math.abs(offset) >= width / 3 || flick
}
